import moment from 'moment'

import { SORT_TYPES, ENROLLMENT_TABLE_COLUMN_HEADERS, enrollmentConnectionStatusesOrderLookupTable, KARDIA_PRO_START_DATE, KARDIA_PRO_END_DATE } from '../../../constants'
import { StandardCompare } from '../../../containers/Utilities'

export const sortEnrollmentIds = (
  sorts = {
    sortBy: ENROLLMENT_TABLE_COLUMN_HEADERS.START_DATE,
    sortDirection: SORT_TYPES.DESC,
  },
  eIds = [],
  enrollmentByIds = {},
  icd10CodeByIds = {},
  billingCodeByIds = {},
) => {
  const {
    sortBy,
    sortDirection,
  } = sorts
  const enrollmentIds = [ ...eIds ]
  let sortedEnrollmentIds = enrollmentIds

  if (enrollmentIds) {
    switch (sortBy) {
    case ENROLLMENT_TABLE_COLUMN_HEADERS.START_DATE:
    case ENROLLMENT_TABLE_COLUMN_HEADERS.END_DATE:
      sortedEnrollmentIds = enrollmentIds.sort((a, b) => StandardCompare.dates(enrollmentByIds[a][sortBy], enrollmentByIds[b][sortBy], sortDirection))
      break
    case ENROLLMENT_TABLE_COLUMN_HEADERS.PRESCRIPTION:
      sortedEnrollmentIds = enrollmentIds.sort((a, b) => {
        const enrollment1 = enrollmentByIds[a]
        const enrollment2 = enrollmentByIds[b]
        const e1Name = enrollment1.connectionTemplateName || ''
        const e2Name = enrollment2.connectionTemplateName || ''
        const status1 = enrollmentConnectionStatusesOrderLookupTable[enrollment1.connectionStatus]
        const status2 = enrollmentConnectionStatusesOrderLookupTable[enrollment2.connectionStatus]

        const result = status1 === status2
        if (result) {
          /* prescription order is the same regardless of conneciton status sort direction */
          return StandardCompare.strings(e1Name, e2Name, sortDirection)
        } else {
          if (sortDirection === SORT_TYPES.ASC) {
            return status1 <= status2 ? -1 : 1
          } else {
            return status1 > status2 ? -1 : 1
          }
        }
      })
      break
    case ENROLLMENT_TABLE_COLUMN_HEADERS.ICD10_CODE:
      sortedEnrollmentIds = enrollmentIds.sort((a, b) => {
        const enrollment1 = enrollmentByIds[a]
        const enrollment2 = enrollmentByIds[b]

        const icd10Code1 = enrollment1.icd10Codes && enrollment1.icd10Codes.length > 0
          ? enrollment1.icd10Codes[0].name
          : ''
        const icd10Code2 = enrollment2.icd10Codes && enrollment2.icd10Codes.length > 0
          ? enrollment2.icd10Codes[0].name
          : ''

        return StandardCompare.strings(icd10Code1, icd10Code2, sortDirection)
      })
      break
    case ENROLLMENT_TABLE_COLUMN_HEADERS.BILLING_CODE:
      sortedEnrollmentIds = enrollmentIds.sort((a, b) => {
        const enrollment1 = enrollmentByIds[a]
        const enrollment2 = enrollmentByIds[b]

        const billingCode1 = enrollment1.billingCodes && enrollment1.billingCodes.length > 0
          ? enrollment1.billingCodes[0].name
          : ''
        const billingCode2 = enrollment2.billingCodes && enrollment2.billingCodes.length > 0
          ? enrollment2.billingCodes[0].name
          : ''

        return StandardCompare.strings(billingCode1, billingCode2, sortDirection)
      })
      break
    case ENROLLMENT_TABLE_COLUMN_HEADERS.ORDER_NUMBER:
      sortedEnrollmentIds = enrollmentIds.sort((a, b) => {
        const enrollment1 = enrollmentByIds[a]
        const enrollment2 = enrollmentByIds[b]

        const orderNumber1 = enrollment1.orderNumber || ''
        const orderNumber2 = enrollment2.orderNumber || ''

        return StandardCompare.strings(orderNumber1, orderNumber2, sortDirection)
      })
      break
    default:
      // No sort, keep previous sort
      break
    }
  }

  return sortedEnrollmentIds
}

export const filterEnrollmentIds = (
  filters = {
    selectedStartStartDate: undefined,
    selectedStartEndDate: undefined,
    selectedEndStartDate: undefined,
    selectedEndEndDate: undefined,
    selectedPrescriptionId: undefined,
    selectedIcd10CodeId: undefined,
    selectedBillingCode: undefined,
    selectedOrderNumber: undefined,
  },
  enrollmentIds = [],
  enrollmentByIds = {},
  icd10CodeByIds = {},
  billingCodeByIds = {},
) => {
  const {
    selectedStartStartDate,
    selectedStartEndDate,
    selectedEndStartDate,
    selectedEndEndDate,
    selectedPrescriptionId,
    selectedIcd10CodeId,
    selectedBillingCode,
    selectedOrderNumber,
  } = filters
  let filteredEnrollmentIds = enrollmentIds

  if (enrollmentIds) {
    if (selectedStartStartDate !== KARDIA_PRO_START_DATE
      || selectedStartEndDate !== KARDIA_PRO_END_DATE
    ) {
      filteredEnrollmentIds = filteredEnrollmentIds
        .filter(id => {
          const e = enrollmentByIds[id]

          if (moment(e.startDate).isBefore(moment(selectedStartStartDate).startOf('day'))
            || moment(e.startDate).isAfter(moment(selectedStartEndDate).endOf('day'))) {
            return false
          }

          // if selected start dates are not default and start date does not exist,
          // remove them from the list
          if ((!moment(selectedStartStartDate).isSame(KARDIA_PRO_START_DATE)
            || !moment(selectedStartEndDate).isSame(KARDIA_PRO_END_DATE))
            && e.startDate === null
          ) {
            return false
          }

          return true
        })
    }

    if (selectedEndStartDate !== KARDIA_PRO_START_DATE
      || selectedEndEndDate !== KARDIA_PRO_END_DATE
    ) {
      filteredEnrollmentIds = filteredEnrollmentIds
        .filter(id => {
          const e = enrollmentByIds[id]

          if (moment(e.endDate).isBefore(moment(selectedEndStartDate).startOf('day'))
            || moment(e.endDate).isAfter(moment(selectedEndEndDate).endOf('day'))) {
            return false
          }

          // if selected start dates are not default and start date does not exist,
          // remove them from the list
          if ((!moment(selectedEndStartDate).isSame(KARDIA_PRO_START_DATE)
            || !moment(selectedEndEndDate).isSame(KARDIA_PRO_END_DATE))
            && e.endDate === null
          ) {
            return false
          }

          return true
        })
    }

    if (selectedPrescriptionId) {
      filteredEnrollmentIds = filteredEnrollmentIds
        .filter(id => {
          const e = enrollmentByIds[id]

          return e.connectionTemplateID === selectedPrescriptionId
        })
    }

    if (selectedIcd10CodeId) {
      filteredEnrollmentIds = filteredEnrollmentIds
        .filter(id => {
          const e = enrollmentByIds[id]
          const icd10CodeId = e.icd10Codes && e.icd10Codes.length > 0
            ? e.icd10Codes[0].id
            : ''

          return icd10CodeId === selectedIcd10CodeId
        })
    }

    if (selectedBillingCode.length > 0) {
      filteredEnrollmentIds = filteredEnrollmentIds
        .filter(id => {
          const e = enrollmentByIds[id]
          const billingCodeId = e.billingCodes && e.billingCodes.length > 0
            ? e.billingCodes[0].id
            : ''
          return selectedBillingCode.some(billingCode => billingCode.id === billingCodeId)
        })
    }

    if (selectedOrderNumber) {
      filteredEnrollmentIds = filteredEnrollmentIds
        .filter(id => {
          const e = enrollmentByIds[id]
          const text = e.orderNumber || ''
          return text.includes(selectedOrderNumber)
        })
    }
  }

  return filteredEnrollmentIds
}



// WEBPACK FOOTER //
// ./src/legacy/containers/Tables/EnrollmentTable/Utilities.js